import { useState, useContext } from 'react';
import { toast } from '../lib/toast.js';
import { useParams } from 'react-router-dom';
import GlobalContext from "../context/GlobalContext.js";
import ResetPasswordAPI from '../functions/API/ResetPasswordAPI';
import PasswordStrengthChecker from '../components/PasswordStrengthChecker';
import { darkenColor } from '../functions/colorUtils.js';
import { ShieldCheck, X, Eye, EyeOff, CheckCircle2 } from 'lucide-react';

const STEPS = [
    { key: 'email', question: "What's your email address?", description: 'Enter the email address associated with your account', placeholder: 'Email address', type: 'email' },
    { key: 'password', question: 'Create a new password', description: 'Choose a strong password to secure your account', placeholder: 'New password', type: 'password' },
];

export default function ResetPassword() {
    const { token } = useParams();
    const { settings } = useContext(GlobalContext);
    const primaryColor = settings?.primary_hex_colour || '#813468';
    const primaryDark = darkenColor(primaryColor);

    const [step, setStep] = useState(0);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [passwordSecure, setPasswordSecure] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const currentStep = STEPS[step];

    function next() {
        if (step === 0) {
            if (!email.trim()) { toast.error('Please enter your email address'); return; }
            setStep(1);
            return;
        }
        submitReset();
    }

    function back() {
        if (step > 0) setStep(s => s - 1);
    }

    async function submitReset() {
        if (!passwordSecure) { toast.error('Your password does not meet the minimum requirements'); return; }
        if (password !== confirmPassword) { toast.error("Passwords don't match"); return; }
        setLoading(true);
        const res = await ResetPasswordAPI(email, token, password, confirmPassword);
        if (res.response?.status === 200) {
            setSuccess(true);
            setTimeout(() => { window.location.href = '/login'; }, 3000);
        } else {
            toast.error('Password reset failed. Please try again.');
        }
        setLoading(false);
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter') { e.preventDefault(); next(); }
    }

    const inputClass = 'w-full rounded-xl border border-gray-200 bg-white px-4 py-3.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-shadow';

    function GradientContent({ className }) {
        return (
            <div className={className}>
                <a
                    href="/login"
                    className="inline-flex items-center gap-1.5 text-white/90 text-sm font-medium hover:text-white transition-colors mb-5"
                >
                    <X size={16} strokeWidth={2.5} />
                    Exit
                </a>

                {/* Progress bar */}
                <div className="flex gap-1.5 mb-8">
                    {STEPS.map((_, i) => (
                        <div
                            key={i}
                            className="flex-1 h-1 rounded-full transition-colors duration-300"
                            style={{ backgroundColor: success || i <= step ? '#fff' : 'rgba(255,255,255,0.25)' }}
                        />
                    ))}
                </div>

                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                        <ShieldCheck size={22} className="text-white" strokeWidth={1.8} />
                    </div>
                    <h1 className="text-2xl font-bold text-white">New password</h1>
                </div>

                <p className="hidden lg:block mt-6 text-white/60 text-sm">
                    Step {step + 1} of {STEPS.length}
                </p>
            </div>
        );
    }

    const formContent = success ? (
        <>
            <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle2 size={32} className="text-green-600" strokeWidth={1.5} />
                </div>
            </div>
            <h2 className="text-xl font-bold text-gray-900 text-center mb-1.5">Password reset</h2>
            <p className="text-sm text-gray-500 text-center mb-8">
                Your password has been updated. Redirecting you to sign in...
            </p>
            <a
                href="/login"
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-semibold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: primaryColor }}
            >
                Sign In
            </a>
        </>
    ) : (
        <>
            <h2 className="text-xl font-bold text-gray-900 mb-1.5">{currentStep.question}</h2>
            <p className="text-sm text-gray-500 mb-6">{currentStep.description}</p>

            {step === 0 ? (
                <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Email address"
                    className={inputClass}
                    autoFocus
                />
            ) : (
                <div className="space-y-3">
                    <div className="relative">
                        <input
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="New password"
                            className={`${inputClass} pr-12`}
                            autoFocus
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                        >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>
                    <div className="relative">
                        <input
                            type={showConfirm ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Confirm new password"
                            className={`${inputClass} pr-12`}
                        />
                        <button
                            type="button"
                            onClick={() => setShowConfirm(!showConfirm)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                        >
                            {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>
                    <PasswordStrengthChecker password={password} passwordSecure={setPasswordSecure} />
                </div>
            )}

            <div className="mt-6 flex gap-3">
                {step > 0 && (
                    <button
                        onClick={back}
                        className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl text-sm font-semibold border-2 transition-opacity hover:opacity-80"
                        style={{ borderColor: primaryColor, color: primaryColor }}
                    >
                        Back
                    </button>
                )}
                <button
                    onClick={next}
                    disabled={loading}
                    className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                    style={{ backgroundColor: primaryColor }}
                >
                    {loading ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : step === 0 ? (
                        'Next'
                    ) : (
                        'Reset Password'
                    )}
                </button>
            </div>

            <p className="mt-8 text-center text-sm text-gray-500">
                Remember your password?{' '}
                <a href="/login" className="font-semibold hover:opacity-80" style={{ color: primaryColor }}>
                    Sign in
                </a>
            </p>
        </>
    );

    return (
        <>
            {/* Mobile */}
            <div className="lg:hidden min-h-screen flex flex-col" style={{ backgroundColor: '#F2F2F7' }}>
                <div
                    className="w-full flex-shrink-0"
                    style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryDark})` }}
                >
                    <GradientContent className="px-5 pt-5 pb-12" />
                </div>
                <div className="flex-1 -mt-6 relative z-10">
                    <div className="bg-[#F2F2F7] rounded-t-[1.5rem] min-h-full">
                        <div className="px-5 pt-7 pb-12">
                            {formContent}
                        </div>
                    </div>
                </div>
            </div>

            {/* Desktop */}
            <div className="hidden lg:flex min-h-screen" style={{ backgroundColor: '#F2F2F7' }}>
                <div
                    className="w-1/2 flex items-center justify-center p-12"
                    style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryDark})` }}
                >
                    <GradientContent className="w-full max-w-md" />
                </div>
                <div className="w-1/2 flex items-center justify-center p-12">
                    <div className="w-full max-w-md">
                        {formContent}
                    </div>
                </div>
            </div>
        </>
    );
}
